// ===============================================================================
// Settings.js - Enhanced User Settings Page for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/Settings.js
// Features:
// - Change Display Name
// - Change Email (with OTP verification)
// - Change Password
// - Security Status
// - Delete Account (with soft delete and recovery)
// ===============================================================================

import React, { useState, useEffect, useRef } from "react";
import "./Settings.css";
import { authAPI, settingsAPI, billingAPI } from "./api";

function Settings({ user, onBack, onLogout }) {
  // ===============================================================================
  // Active Section State
  // ===============================================================================
  const [activeSection, setActiveSection] = useState(null);
  
  // ===============================================================================
  // Change Name State
  // ===============================================================================
  const [newName, setNewName] = useState(user?.name || "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMessage, setNameMessage] = useState({ type: "", text: "" });
  
  // ===============================================================================
  // Change Email State
  // ===============================================================================
  const [emailStep, setEmailStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailOtp, setEmailOtp] = useState(["", "", "", "", "", ""]);
  const [emailMasked, setEmailMasked] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState({ type: "", text: "" });
  const emailOtpRefs = useRef([]);
  
  // ===============================================================================
  // Change Password State
  // ===============================================================================
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });
  
  // ===============================================================================
  // Security Status State
  // ===============================================================================
  const [securityStatus, setSecurityStatus] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  
  // ===============================================================================
  // Delete Account State
  // ===============================================================================
  const [deleteStep, setDeleteStep] = useState(0); // 0=check, 1=transfer, 2=password, 3=otp, 4=done
  const [deleteCheck, setDeleteCheck] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteOtp, setDeleteOtp] = useState(["", "", "", "", "", ""]);
  const [deleteMaskedEmail, setDeleteMaskedEmail] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState({ type: "", text: "" });
  const [adminTransfers, setAdminTransfers] = useState({}); // { groupId: selectedUserId }
  const deleteOtpRefs = useRef([]);
  const [deletionDate, setDeletionDate] = useState(null);

  // ===============================================================================
  // Billing & Payments State
  // ===============================================================================
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // ===============================================================================
  // Load Security Status on Mount
  // ===============================================================================
  useEffect(() => {
    loadSecurityStatus();
  }, []);

  const loadSecurityStatus = async () => {
    setSecurityLoading(true);
    try {
      const res = await settingsAPI.getSecurityStatus();
      setSecurityStatus(res.data);
    } catch (err) {
      console.error("Failed to load security status:", err);
    }
    setSecurityLoading(false);
  };
  // ===============================================================================
  // Format Payment Method
  // ===============================================================================
  const formatPaymentMethod = (method) => {
    const methods = {
      "visa": "💳 Visa",
      "mastercard": "💳 Mastercard",
      "paypal": "💰 PayPal",
      "amex": "💳 Amex",
      "apple_pay": "🍎 Apple Pay",
      "google_pay": "📱 Google Pay",
      "free_trial": "🎁 Free Trial"
    };
    return methods[method?.toLowerCase()] || method;
  };

  // ===============================================================================
  // Load Billing History
  // ===============================================================================
  const loadBillingHistory = async () => {
    setBillingLoading(true);
    setBillingError("");
    try {
      const res = await billingAPI.getHistory();
      if (res.data?.ok) {
        setBillingHistory(res.data.history || []);
      }
    } catch (err) {
      console.error("Failed to load billing history:", err);
      setBillingError("Failed to load billing history");
    }
    setBillingLoading(false);
  };

  // ===============================================================================
  // Download Invoice PDF
  // ===============================================================================
  const handleDownloadInvoice = async (upgradeId, invoiceNumber) => {
    setDownloadingInvoice(upgradeId);
    try {
      const res = await billingAPI.downloadInvoicePdf(upgradeId);
      
      // Create blob URL and trigger download
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SplitMon_Invoice_${invoiceNumber.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download invoice:", err);
      alert("Failed to download invoice. Please try again.");
    }
    setDownloadingInvoice(null);
  };
  // ===============================================================================
  // OTP Input Handlers
  // ===============================================================================
  const handleOtpChange = (otpArray, setOtp, refs, index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split("");
      const newOtp = [...otpArray];
      pastedCode.forEach((digit, i) => {
        if (i < 6 && /^\d$/.test(digit)) {
          newOtp[i] = digit;
        }
      });
      setOtp(newOtp);
      const lastIndex = Math.min(pastedCode.length - 1, 5);
      refs.current[lastIndex]?.focus();
      return;
    }
    
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtp(newOtp);
    
    if (value && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (otpArray, refs, index, e) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  // ===============================================================================
  // Change Name Handler
  // ===============================================================================
  const handleChangeName = async (e) => {
    e.preventDefault();
    setNameMessage({ type: "", text: "" });
    
    if (!newName.trim()) {
      setNameMessage({ type: "error", text: "Name cannot be empty" });
      return;
    }
    
    if (newName.trim() === user?.name) {
      setNameMessage({ type: "error", text: "Name is the same as current" });
      return;
    }
    
    setNameLoading(true);
    
    try {
      await settingsAPI.changeName(newName.trim());
      setNameMessage({ type: "success", text: "Display name updated successfully!" });
      // Update local user state if needed
    } catch (err) {
      setNameMessage({ type: "error", text: err?.response?.data?.detail || "Failed to update name" });
    }
    
    setNameLoading(false);
  };

  // ===============================================================================
  // Change Email Handlers
  // ===============================================================================
  const handleEmailRequestOTP = async (e) => {
    e.preventDefault();
    setEmailMessage({ type: "", text: "" });
    
    if (!newEmail.trim()) {
      setEmailMessage({ type: "error", text: "Please enter a new email address" });
      return;
    }
    
    if (!emailPassword) {
      setEmailMessage({ type: "error", text: "Please enter your current password" });
      return;
    }
    
    setEmailLoading(true);
    
    try {
      const res = await settingsAPI.changeEmailRequest(newEmail.trim(), emailPassword);
      setEmailMasked(res.data.email);
      setEmailStep(2);
      setEmailMessage({ type: "success", text: "Verification code sent to your new email!" });
      setTimeout(() => emailOtpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setEmailMessage({ type: "error", text: err?.response?.data?.detail || "Failed to send verification code" });
    }
    
    setEmailLoading(false);
  };

  const handleEmailVerifyOTP = async (e) => {
    e.preventDefault();
    setEmailMessage({ type: "", text: "" });
    
    const code = emailOtp.join("");
    if (code.length !== 6) {
      setEmailMessage({ type: "error", text: "Please enter the 6-digit code" });
      return;
    }
    
    setEmailLoading(true);
    
    try {
      await settingsAPI.changeEmailVerify(newEmail.trim(), code);
      setEmailMessage({ type: "success", text: "Email updated successfully!" });
      setEmailStep(1);
      setNewEmail("");
      setEmailPassword("");
      setEmailOtp(["", "", "", "", "", ""]);
      setActiveSection(null);
    } catch (err) {
      setEmailMessage({ type: "error", text: err?.response?.data?.detail || "Invalid verification code" });
      setEmailOtp(["", "", "", "", "", ""]);
      emailOtpRefs.current[0]?.focus();
    }
    
    setEmailLoading(false);
  };

  // ===============================================================================
  // Change Password Handler
  // ===============================================================================
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: "", text: "" });
    
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: "error", text: "New passwords don't match" });
      return;
    }
    
    if (newPassword.length < 8) {
      setPasswordMessage({ type: "error", text: "Password must be at least 8 characters" });
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordMessage({ type: "success", text: "Password changed successfully!" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordMessage({ type: "error", text: err?.response?.data?.detail || "Failed to change password" });
    }
    
    setPasswordLoading(false);
  };

  // ===============================================================================
  // Delete Account Handlers
  // ===============================================================================
  const handleDeleteAccountCheck = async () => {
    setDeleteMessage({ type: "", text: "" });
    setDeleteLoading(true);
    
    try {
      const res = await settingsAPI.deleteAccountCheck();
      setDeleteCheck(res.data);
      
      if (res.data.has_balance_issues) {
        setDeleteStep(0); // Show balance issues
      } else if (res.data.needs_admin_transfer) {
        setDeleteStep(1); // Show admin transfer
        // Initialize transfer selections
        const transfers = {};
        res.data.groups_as_admin.forEach(g => {
          transfers[`${g.type}_${g.id}`] = null;
        });
        setAdminTransfers(transfers);
      } else {
        setDeleteStep(2); // Go to password step
      }
    } catch (err) {
      setDeleteMessage({ type: "error", text: err?.response?.data?.detail || "Failed to check account status" });
    }
    
    setDeleteLoading(false);
  };

  const handleTransferAdmin = async (group) => {
    const key = `${group.type}_${group.id}`;
    const newAdminId = adminTransfers[key];
    
    if (!newAdminId) {
      setDeleteMessage({ type: "error", text: `Please select a new admin for "${group.name}"` });
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      await settingsAPI.transferAdmin(group.id, group.type, newAdminId);
      setDeleteMessage({ type: "success", text: `Admin transferred for "${group.name}"` });
      
      // Refresh the check
      await handleDeleteAccountCheck();
    } catch (err) {
      setDeleteMessage({ type: "error", text: err?.response?.data?.detail || "Failed to transfer admin" });
    }
    
    setDeleteLoading(false);
  };

  const handleDeleteInitiate = async (e) => {
    e.preventDefault();
    setDeleteMessage({ type: "", text: "" });
    
    if (!deletePassword) {
      setDeleteMessage({ type: "error", text: "Please enter your password" });
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      const res = await settingsAPI.deleteAccountInitiate(deletePassword);
      setDeleteMaskedEmail(res.data.email);
      setDeleteStep(3);
      setDeleteMessage({ type: "success", text: "Verification code sent to your email!" });
      setTimeout(() => deleteOtpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setDeleteMessage({ type: "error", text: err?.response?.data?.detail || "Failed to initiate deletion" });
    }
    
    setDeleteLoading(false);
  };

  const handleDeleteConfirm = async (e) => {
    e.preventDefault();
    setDeleteMessage({ type: "", text: "" });
    
    const code = deleteOtp.join("");
    if (code.length !== 6) {
      setDeleteMessage({ type: "error", text: "Please enter the 6-digit code" });
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      const res = await settingsAPI.deleteAccountConfirm(code);
      setDeletionDate(res.data.deletion_date);
      setDeleteStep(4);
    } catch (err) {
      setDeleteMessage({ type: "error", text: err?.response?.data?.detail || "Invalid verification code" });
      setDeleteOtp(["", "", "", "", "", ""]);
      deleteOtpRefs.current[0]?.focus();
    }
    
    setDeleteLoading(false);
  };

  // ===============================================================================
  // Format Date Helper
  // ===============================================================================
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="settings-page">
      {/* Header */}
    
      {/* Profile Card */}
      <div className="settings-card profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="profile-info">
            <h2>{user?.name || "User"}</h2>
            <p className="profile-username">@{user?.username}</p>
            <p className="profile-email">{user?.email}</p>
          </div>
          <div className="profile-badge">
            {user?.is_premium ? (
              <span className="badge badge-premium">⭐ Premium</span>
            ) : (
              <span className="badge badge-free">Free Plan</span>
            )}
          </div>
        </div>
      </div>

      {/* Security Status Card */}
      <div className="settings-card">
        <div className="card-header">
          <h3>🔒 Security Status</h3>
        </div>
        <div className="security-status">
          {securityLoading ? (
            <div className="loading-spinner">Loading...</div>
          ) : securityStatus ? (
            <div className="status-grid">
              <div className="status-item">
                <span className={`status-indicator ${securityStatus.email_verified ? 'verified' : 'unverified'}`}>
                  {securityStatus.email_verified ? '✓' : '✗'}
                </span>
                <span className="status-label">Email Verified</span>
              </div>
              <div className="status-item">
                <span className="status-indicator verified">✓</span>
                <span className="status-label">Password Protected</span>
              </div>
              <div className="status-item">
                <span className="status-value">
                  {securityStatus.membership_type}
                  {securityStatus.is_premium && securityStatus.premium_days_remaining !== null && (
                    <span className="premium-days">
                      ({securityStatus.premium_days_remaining} days left)
                    </span>
                  )}
                </span>
                <span className="status-label">Membership</span>
              </div>
              <div className="status-item">
                <span className="status-value">{formatDate(securityStatus.account_created)}</span>
                <span className="status-label">Member Since</span>
              </div>
            </div>
          ) : (
            <p className="error-text">Failed to load security status</p>
          )}
        </div>
      </div>

      {/* Account Settings */}
      <div className="settings-card">
        <div className="card-header">
          <h3>👤 Account Settings</h3>
        </div>
        
        {/* Change Display Name */}
        <div className="settings-section">
          <div 
            className={`section-header ${activeSection === 'name' ? 'active' : ''}`}
            onClick={() => setActiveSection(activeSection === 'name' ? null : 'name')}
          >
            <div className="section-title">
              <span className="section-icon">✏️</span>
              <span>Change Display Name</span>
            </div>
            <span className="section-arrow">{activeSection === 'name' ? '▼' : '▶'}</span>
          </div>
          
          {activeSection === 'name' && (
            <form className="section-content" onSubmit={handleChangeName}>
              <div className="form-group">
                <label>New Display Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter new display name"
                  maxLength={100}
                />
              </div>
              
              {nameMessage.text && (
                <div className={`form-message ${nameMessage.type}`}>
                  {nameMessage.text}
                </div>
              )}
              
              <button type="submit" className="btn btn-primary" disabled={nameLoading}>
                {nameLoading ? "Updating..." : "Update Name"}
              </button>
            </form>
          )}
        </div>

        {/* Change Email */}
        <div className="settings-section">
          <div 
            className={`section-header ${activeSection === 'email' ? 'active' : ''}`}
            onClick={() => setActiveSection(activeSection === 'email' ? null : 'email')}
          >
            <div className="section-title">
              <span className="section-icon">📧</span>
              <span>Change Email</span>
            </div>
            <span className="section-arrow">{activeSection === 'email' ? '▼' : '▶'}</span>
          </div>
          
          {activeSection === 'email' && (
            <div className="section-content">
              {emailStep === 1 ? (
                <form onSubmit={handleEmailRequestOTP}>
                  <div className="form-group">
                    <label>New Email Address</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email address"
                    />
                  </div>
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      placeholder="Enter your current password"
                    />
                  </div>
                  
                  {emailMessage.text && (
                    <div className={`form-message ${emailMessage.type}`}>
                      {emailMessage.text}
                    </div>
                  )}
                  
                  <button type="submit" className="btn btn-primary" disabled={emailLoading}>
                    {emailLoading ? "Sending Code..." : "Send Verification Code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleEmailVerifyOTP}>
                  <p className="otp-subtitle">
                    Enter the 6-digit code sent to<br />
                    <strong>{emailMasked}</strong>
                  </p>
                  
                  <div className="otp-inputs">
                    {emailOtp.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => emailOtpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(emailOtp, setEmailOtp, emailOtpRefs, index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(emailOtp, emailOtpRefs, index, e)}
                        className="otp-input"
                      />
                    ))}
                  </div>
                  
                  {emailMessage.text && (
                    <div className={`form-message ${emailMessage.type}`}>
                      {emailMessage.text}
                    </div>
                  )}
                  
                  <div className="button-group">
                    <button type="submit" className="btn btn-primary" disabled={emailLoading}>
                      {emailLoading ? "Verifying..." : "Verify & Update Email"}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setEmailStep(1);
                        setEmailOtp(["", "", "", "", "", ""]);
                        setEmailMessage({ type: "", text: "" });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="settings-section">
          <div 
            className={`section-header ${activeSection === 'password' ? 'active' : ''}`}
            onClick={() => setActiveSection(activeSection === 'password' ? null : 'password')}
          >
            <div className="section-title">
              <span className="section-icon">🔑</span>
              <span>Change Password</span>
            </div>
            <span className="section-arrow">{activeSection === 'password' ? '▼' : '▶'}</span>
          </div>
          
          {activeSection === 'password' && (
            <form className="section-content" onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              
              {passwordMessage.text && (
                <div className={`form-message ${passwordMessage.type}`}>
                  {passwordMessage.text}
                </div>
              )}
              
              <button type="submit" className="btn btn-primary" disabled={passwordLoading}>
                {passwordLoading ? "Changing..." : "Change Password"}
              </button>
            </form>
          )}
        </div>
      </div>
{/* Billing & Payments */}
      <div className="settings-card">
        <div className="card-header">
          <h3>💳 Billing & Payments</h3>
        </div>
        
        <div className="settings-section">
          <div 
            className={`section-header ${activeSection === 'billing' ? 'active' : ''}`}
            onClick={() => {
              if (activeSection !== 'billing') {
                setActiveSection('billing');
                loadBillingHistory();
              } else {
                setActiveSection(null);
              }
            }}
          >
            <div className="section-title">
              <span className="section-icon">🧾</span>
              <span>Payment History & Invoices</span>
            </div>
            <span className="section-arrow">{activeSection === 'billing' ? '▼' : '▶'}</span>
          </div>
          
          {activeSection === 'billing' && (
            <div className="section-content">
              {billingLoading ? (
                <div className="loading-spinner">Loading billing history...</div>
              ) : billingError ? (
                <div className="form-message error">{billingError}</div>
              ) : billingHistory.length === 0 ? (
                <div className="empty-billing">
                  <p>No payment history yet.</p>
                </div>
              ) : (
                <div className="billing-table-wrapper">
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Duration</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingHistory.map((item) => (
                        <tr key={item.id}>
                          <td>{new Date(item.date_of_purchase).toLocaleDateString()}</td>
                          <td>{item.total_months} month{item.total_months !== 1 ? 's' : ''}</td>
                          <td className={item.total_pay === 0 ? 'free-amount' : ''}>
                            {item.total_pay === 0 ? 'FREE' : `€${item.total_pay.toFixed(2)}`}
                          </td>
                          <td>{formatPaymentMethod(item.payment_method)}</td>
                          <td>
                            <button 
                              className="invoice-link"
                              onClick={() => handleDownloadInvoice(item.id, item.invoice_number)}
                              disabled={downloadingInvoice === item.id}
                            >
                              {downloadingInvoice === item.id ? (
                                <span className="btn-spinner-small"></span>
                              ) : (
                                <>📄 View Invoice</>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="settings-card danger-card">
        <div className="card-header">
          <h3>⚠️ Danger Zone</h3>
        </div>
        
        <div className="settings-section">
          <div 
            className={`section-header danger ${activeSection === 'delete' ? 'active' : ''}`}
            onClick={() => {
              if (activeSection !== 'delete') {
                setActiveSection('delete');
                setDeleteStep(0);
                setDeleteCheck(null);
                handleDeleteAccountCheck();
              } else {
                setActiveSection(null);
              }
            }}
          >
            <div className="section-title">
              <span className="section-icon">🗑️</span>
              <span>Delete Account</span>
            </div>
            <span className="section-arrow">{activeSection === 'delete' ? '▼' : '▶'}</span>
          </div>
          
          {activeSection === 'delete' && (
            <div className="section-content delete-content">
              {deleteLoading && !deleteCheck && (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Checking account status...</p>
                </div>
              )}
              
              {/* Step 0: Balance Issues */}
              {deleteStep === 0 && deleteCheck && deleteCheck.has_balance_issues && (
                <div className="delete-step">
                  <div className="warning-box">
                    <h4>⚠️ Outstanding Balances</h4>
                    <p>You must settle all balances to zero before deleting your account.</p>
                  </div>
                  
                  {deleteCheck.personal_groups_with_balance.length > 0 && (
                    <div className="balance-list">
                      <h5>Personal Groups:</h5>
                      {deleteCheck.personal_groups_with_balance.map(g => (
                        <div key={`personal_${g.id}`} className="balance-item">
                          <span className="group-name">{g.name}</span>
                          <span className={`balance ${g.balance >= 0 ? 'positive' : 'negative'}`}>
                            {g.balance >= 0 ? '+' : ''}{g.balance.toFixed(2)} {g.currency}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {deleteCheck.business_groups_with_balance.length > 0 && (
                    <div className="balance-list">
                      <h5>Business Groups:</h5>
                      {deleteCheck.business_groups_with_balance.map(g => (
                        <div key={`business_${g.id}`} className="balance-item">
                          <span className="group-name">{g.name}</span>
                          <span className={`balance ${g.balance >= 0 ? 'positive' : 'negative'}`}>
                            {g.balance >= 0 ? '+' : ''}{g.balance.toFixed(2)} {g.currency}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 1: Admin Transfer */}
              {deleteStep === 1 && deleteCheck && deleteCheck.needs_admin_transfer && (
                <div className="delete-step">
                  <div className="info-box">
                    <h4>👑 Transfer Admin Rights</h4>
                    <p>You are the admin of the following groups. Please select a new admin for each group.</p>
                  </div>
                  
                  {deleteCheck.groups_as_admin.map(group => (
                    <div key={`${group.type}_${group.id}`} className="admin-transfer-item">
                      <div className="group-info">
                        <span className="group-name">{group.name}</span>
                        <span className="group-type">{group.type}</span>
                      </div>
                      <div className="transfer-select">
                        <select
                          value={adminTransfers[`${group.type}_${group.id}`] || ""}
                          onChange={(e) => setAdminTransfers({
                            ...adminTransfers,
                            [`${group.type}_${group.id}`]: parseInt(e.target.value)
                          })}
                        >
                          <option value="">Select new admin...</option>
                          {group.members.map(m => (
                            <option key={m.id} value={m.id}>{m.name} (@{m.username})</option>
                          ))}
                        </select>
                        <button 
                          className="btn btn-small btn-primary"
                          onClick={() => handleTransferAdmin(group)}
                          disabled={deleteLoading || !adminTransfers[`${group.type}_${group.id}`]}
                        >
                          Transfer
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {deleteMessage.text && (
                    <div className={`form-message ${deleteMessage.type}`}>
                      {deleteMessage.text}
                    </div>
                  )}
                </div>
              )}
              
              {/* Show groups to leave and solo groups */}
              {deleteStep >= 1 && deleteCheck && !deleteCheck.has_balance_issues && !deleteCheck.needs_admin_transfer && (
                <>
                  {deleteCheck.groups_as_member.length > 0 && (
                    <div className="info-box light">
                      <h4>📋 Groups You'll Leave</h4>
                      <ul className="group-list">
                        {deleteCheck.groups_as_member.map(g => (
                          <li key={`member_${g.type}_${g.id}`}>{g.name} ({g.type})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {deleteCheck.solo_groups.length > 0 && (
                    <div className="warning-box light">
                      <h4>⚠️ Groups That Will Be Deleted</h4>
                      <p>You are the only member of these groups. They will be permanently deleted:</p>
                      <ul className="group-list">
                        {deleteCheck.solo_groups.map(g => (
                          <li key={`solo_${g.type}_${g.id}`}>
                            {g.name} ({g.type}) - {g.expense_count} expenses
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              
              {/* Step 2: Password Verification */}
              {deleteStep === 2 && (
                <form className="delete-step" onSubmit={handleDeleteInitiate}>
                  <div className="warning-box">
                    <h4>🔐 Confirm Your Identity</h4>
                    <p>Enter your password to continue with account deletion.</p>
                  </div>
                  
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                    />
                  </div>
                  
                  {deleteMessage.text && (
                    <div className={`form-message ${deleteMessage.type}`}>
                      {deleteMessage.text}
                    </div>
                  )}
                  
                  <button type="submit" className="btn btn-danger" disabled={deleteLoading}>
                    {deleteLoading ? "Verifying..." : "Continue Deletion"}
                  </button>
                </form>
              )}
              
              {/* Step 3: OTP Verification */}
              {deleteStep === 3 && (
                <form className="delete-step" onSubmit={handleDeleteConfirm}>
                  <div className="warning-box">
                    <h4>📧 Final Verification</h4>
                    <p>Enter the 6-digit code sent to <strong>{deleteMaskedEmail}</strong></p>
                  </div>
                  
                  <div className="otp-inputs">
                    {deleteOtp.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => deleteOtpRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(deleteOtp, setDeleteOtp, deleteOtpRefs, index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(deleteOtp, deleteOtpRefs, index, e)}
                        className="otp-input danger"
                      />
                    ))}
                  </div>
                  
                  {deleteMessage.text && (
                    <div className={`form-message ${deleteMessage.type}`}>
                      {deleteMessage.text}
                    </div>
                  )}
                  
                  <button type="submit" className="btn btn-danger" disabled={deleteLoading}>
                    {deleteLoading ? "Processing..." : "Confirm Deletion"}
                  </button>
                </form>
              )}

              {/* Step 4: Deletion Confirmed */}
              {deleteStep === 4 && (
                <div className="deletion-overlay">
                  <div className="deletion-overlay-content">
                    <div className="deletion-icon">✓</div>
                    <h2>Account Scheduled for Deletion</h2>
                    <p className="deletion-date">
                      Your account will be permanently deleted on<br />
                      <strong>{formatDate(deletionDate)}</strong>
                    </p>
                    <div className="deletion-recovery-note">
                      <p><strong>Changed your mind?</strong></p>
                      <p>Simply log in within 10 days to recover your account.</p>
                    </div>
                    <button className="btn-logout-now" onClick={onLogout}>
                      Logout Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* About Section */}
      <div className="settings-card">
        <div className="card-header">
          <h3>ℹ️ About</h3>
        </div>
        <div className="about-content">
          <p className="app-name">SplitMon - Split Monitoring App</p>
          <p className="app-version">Version 2.0.0</p>
          <p className="app-copyright">© 2025 CSTN Apps</p>
        </div>
      </div>
    </div>
  );
}

export default Settings;

